import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, FileSignature, Upload, Check, Download, Pencil, Bell } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

const inputCls = 'w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]';

const STATUS = {
  shipped: { label: 'Kargolandı (teslim bekleniyor)', cls: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  completed: { label: 'Teslim Alındı / Tamamlandı', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
};

const bool = (v) => v === true || v === 'true' || v === 'True';

export default function SignatureManager() {
  const [enabled, setEnabled] = useState(false);
  const [docs, setDocs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', is_required: false, is_active: true });
  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null); // {file_path, original_name}
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [warning, setWarning] = useState({ enabled: false, message: '' });
  const [savingWarning, setSavingWarning] = useState(false);

  const fetchAll = useCallback(() => {
    api.get('/admin/signature-documents').then(r => setDocs(r.data)).catch(() => {});
    api.get('/admin/signature-submissions').then(r => setSubs(r.data)).catch(() => {});
    api.get('/admin/signature-warning').then(r => setWarning({ enabled: !!r.data.enabled, message: r.data.message || '' })).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/membership/status').then(r => {
      const on = r.data?.wet_signature_enabled === true;
      setEnabled(on);
      if (on) fetchAll();
    }).catch(() => {});
  }, [fetchAll]);

  const reset = () => {
    setForm({ title: '', description: '', is_required: false, is_active: true });
    setFile(null); setExistingFile(null); setEditingId(null); setShowForm(false);
  };

  const openNew = () => { reset(); setShowForm(true); };

  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({ title: d.title || '', description: d.description || '', is_required: bool(d.is_required), is_active: bool(d.is_active) });
    setExistingFile({ file_path: d.file_path, original_name: d.original_name });
    setFile(null);
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Belge başlığı gerekli'); return; }
    if (!editingId && !file) { toast.error('Lütfen bir dosya seçin'); return; }
    setIsSubmitting(true);
    try {
      let file_path = existingFile?.file_path || '';
      let original_name = existingFile?.original_name || '';
      if (file) {
        const fd = new FormData();
        fd.append('file', file);
        const up = await api.post('/uploads', fd);
        file_path = up.data.path;
        original_name = file.name;
      }
      const payload = {
        title: form.title, description: form.description,
        file_path, original_name,
        is_active: form.is_active, is_required: form.is_required,
        position: editingId ? (docs.find(x => x.id === editingId)?.position ?? 0) : docs.length,
      };
      if (editingId) {
        await api.put(`/admin/signature-documents/${editingId}`, payload);
        toast.success('Belge güncellendi');
      } else {
        await api.post('/admin/signature-documents', payload);
        toast.success('Belge eklendi');
      }
      reset(); fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Yükleme hatası'); }
    finally { setIsSubmitting(false); }
  };

  const removeDoc = async (id) => {
    if (!window.confirm('Bu belgeyi silmek istediğinize emin misiniz?')) return;
    try { await api.delete(`/admin/signature-documents/${id}`); toast.success('Silindi'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Silme hatası'); }
  };

  const complete = async (subId) => {
    try { await api.post(`/admin/signature-submissions/${subId}/complete`); toast.success('Teslim alındı olarak işaretlendi'); fetchAll(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const saveWarning = async () => {
    setSavingWarning(true);
    try {
      await api.put('/admin/signature-warning', warning);
      toast.success('Uyarı ayarı kaydedildi');
    } catch (err) { toast.error(err.response?.data?.detail || 'Kaydetme hatası'); }
    finally { setSavingWarning(false); }
  };

  if (!enabled) return null;

  return (
    <div className="mt-12 pt-8 border-t border-border" data-testid="admin-signature-manager">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="min-w-0">
          <h2 className="font-heading text-xl font-semibold flex items-center gap-2"><FileSignature className="w-5 h-5 text-[hsl(var(--gold))]" /> Islak İmza Belgeleri</h2>
          <p className="text-sm text-muted-foreground mt-1">Üyelerin indirip imzalayarak adrese kargolayacağı belgeler.</p>
        </div>
        <button onClick={openNew} data-testid="add-signature-doc"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] shrink-0">
          <Plus className="w-4 h-4" /> Belge Ekle
        </button>
      </div>

      {showForm && (
        <div className="mb-8 p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Başlık *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className={inputCls} placeholder="ör. Üyelik Başvuru Formu" data-testid="sig-title" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Açıklama</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className={inputCls} placeholder="Üyeye gösterilecek açıklama / talimat" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Dosya {editingId ? '(değiştirmek için seçin)' : '*'} (PDF, Word, resim)</label>
              <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={e => setFile(e.target.files[0])} className={inputCls} data-testid="sig-file" />
              {file
                ? <p className="text-xs text-muted-foreground mt-1">{file.name}</p>
                : existingFile?.original_name && <p className="text-xs text-muted-foreground mt-1">Mevcut: {existingFile.original_name}</p>}
            </div>
            <div className="flex flex-wrap items-center gap-6">
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={form.is_required} onChange={e => setForm(f => ({ ...f, is_required: e.target.checked }))}
                  className="w-4 h-4 accent-[hsl(var(--gold))]" data-testid="sig-required" />
                Zorunlu belge
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  className="w-4 h-4 accent-[hsl(var(--gold))]" data-testid="sig-active" />
                Aktif (üyeye göster)
              </label>
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={isSubmitting} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="sig-save">
                <Upload className="w-4 h-4" /> {isSubmitting ? 'Kaydediliyor...' : (editingId ? 'Güncelle' : 'Kaydet')}
              </button>
              <button type="button" onClick={reset} className="px-6 py-2.5 rounded-lg border border-input text-sm hover:bg-secondary">İptal</button>
            </div>
          </form>
        </div>
      )}

      {/* Templates */}
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Belgeler</h3>
      {docs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Henüz belge yüklenmedi</div>
      ) : (
        <div className="space-y-3 mb-8">
          {docs.map(d => (
            <div key={d.id} className="p-4 rounded-xl bg-card border border-border flex items-center gap-4" data-testid="signature-doc-item">
              <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center shrink-0"><FileSignature className="w-5 h-5 text-[hsl(var(--gold))]" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm flex items-center gap-2 flex-wrap">
                  {d.title}
                  {bool(d.is_required) && <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-destructive/10 text-destructive">Zorunlu</span>}
                </div>
                {d.description && <div className="text-xs text-muted-foreground truncate">{d.description}</div>}
              </div>
              <span className={`px-2 py-0.5 text-xs rounded-full shrink-0 ${bool(d.is_active) ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' : 'bg-secondary text-muted-foreground'}`}>
                {bool(d.is_active) ? 'Aktif' : 'Pasif'}
              </span>
              <a href={getFileUrl(d.file_path)} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-secondary shrink-0" title="İndir"><Download className="w-4 h-4" /></a>
              <button onClick={() => openEdit(d)} className="p-2 rounded-lg hover:bg-secondary shrink-0" title="Düzenle" data-testid="sig-edit-btn"><Pencil className="w-4 h-4" /></button>
              <button onClick={() => removeDoc(d.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive shrink-0" title="Sil"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {/* Warning message settings */}
      <div className="mb-8 p-6 rounded-2xl bg-card border border-border" data-testid="sig-warning-card">
        <div className="flex items-center gap-2 mb-1">
          <Bell className="w-4 h-4 text-[hsl(var(--gold))]" />
          <h3 className="font-heading text-base font-semibold">Uyarı Metni (Popup)</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">Kargoladım işaretlemeyen üyelere gösterilecek "resmi kayıt tamamlanmadı" uyarısı.</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer select-none mb-3">
          <input type="checkbox" checked={warning.enabled} onChange={e => setWarning(w => ({ ...w, enabled: e.target.checked }))}
            className="w-4 h-4 accent-[hsl(var(--gold))]" data-testid="sig-warning-enabled" />
          Uyarıyı göster
        </label>
        <textarea value={warning.message} onChange={e => setWarning(w => ({ ...w, message: e.target.value }))} rows={4} className={inputCls}
          placeholder="ör. Resmi üyelik kaydınız henüz tamamlanmadı. Lütfen ıslak imzalı belgelerinizi indirip imzalayarak adresimize kargolayın ve panelinizden 'Kargoladım' butonuna basın."
          data-testid="sig-warning-message" />
        <div className="mt-3">
          <button onClick={saveWarning} disabled={savingWarning} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium disabled:opacity-50" data-testid="sig-warning-save">
            {savingWarning ? 'Kaydediliyor...' : 'Uyarıyı Kaydet'}
          </button>
        </div>
      </div>

      {/* Submissions */}
      <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">Gönderimler</h3>
      {subs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Henüz kargo bildirimi yok</div>
      ) : (
        <div className="space-y-3">
          {subs.map(s => {
            const st = STATUS[s.status] || STATUS.shipped;
            return (
              <div key={s.id} className="p-4 rounded-xl bg-card border border-border flex flex-wrap items-center gap-3" data-testid="signature-submission-item">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{s.first_name} {s.last_name}</div>
                  <div className="text-xs text-muted-foreground">
                    {s.email}
                    {s.shipping_company ? ` · Kargo: ${s.shipping_company}` : ''}
                    {s.tracking_note ? ` · Takip: ${s.tracking_note}` : ''}
                  </div>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded-full ${st.cls}`}>{st.label}</span>
                {s.status !== 'completed' && (
                  <button onClick={() => complete(s.id)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-medium hover:bg-green-700" data-testid="sig-complete-btn">
                    <Check className="w-3.5 h-3.5" /> Teslim Alındı
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
